import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { DICTIONARIES, en, type TranslationKey } from "@/i18n/dictionaries";
import { useAuth } from "@/lib/auth-context";

export interface MemberPreferences {
  preferred_language: string;
  preferred_currency: string;
  content_languages: string[];
  only_my_languages: boolean;
  include_translatable: boolean;
  timezone: string;
  country_code: string | null;
}

export const DEFAULT_PREFERENCES: MemberPreferences = {
  preferred_language: "en",
  preferred_currency: "GBP",
  content_languages: ["en"],
  only_my_languages: false,
  include_translatable: true,
  timezone: "UTC",
  country_code: null,
};

interface I18nValue {
  language: string;
  currency: string;
  preferences: MemberPreferences;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
  setPreferences: (patch: Partial<MemberPreferences>) => Promise<void>;
}

const I18nContext = React.createContext<I18nValue | undefined>(undefined);

function interpolate(template: string, vars?: Record<string, string | number>) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(vars[key] ?? `{${key}}`));
}

/** Guesses sensible defaults for a first-time visitor from their browser. */
function guessDefaults(): Partial<MemberPreferences> {
  if (typeof navigator === "undefined") return {};
  const code = (navigator.language || "en").slice(0, 2).toLowerCase();
  const timezone =
    typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
  return {
    preferred_language: code in DICTIONARIES ? code : "en",
    content_languages: [code in DICTIONARIES ? code : "en"],
    timezone: timezone || "UTC",
  };
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [guest, setGuest] = React.useState<MemberPreferences>(DEFAULT_PREFERENCES);

  React.useEffect(() => {
    if (!user) setGuest((current) => ({ ...current, ...guessDefaults() }));
  }, [user]);

  const { data: stored } = useQuery({
    queryKey: ["member-preferences", user?.id],
    enabled: Boolean(user?.id),
    queryFn: async (): Promise<MemberPreferences> => {
      const { data, error } = await supabase
        .from("user_preferences")
        .select(
          "preferred_language, preferred_currency, content_languages, only_my_languages, include_translatable, timezone, country_code",
        )
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      if (data) return data as MemberPreferences;
      // First sign-in after the global update: create the row from browser hints.
      const seeded = { ...DEFAULT_PREFERENCES, ...guessDefaults() };
      await supabase.from("user_preferences").insert({ user_id: user!.id, ...seeded });
      return seeded;
    },
  });

  const preferences = user ? (stored ?? DEFAULT_PREFERENCES) : guest;

  const setPreferences = React.useCallback(
    async (patch: Partial<MemberPreferences>) => {
      if (!user) {
        setGuest((current) => ({ ...current, ...patch }));
        return;
      }
      const { error } = await supabase
        .from("user_preferences")
        .upsert({ user_id: user.id, ...preferences, ...patch }, { onConflict: "user_id" });
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ["member-preferences"] });
    },
    [user, preferences, queryClient],
  );

  const value = React.useMemo<I18nValue>(() => {
    const dict = DICTIONARIES[preferences.preferred_language] ?? {};
    return {
      language: preferences.preferred_language,
      currency: preferences.preferred_currency,
      preferences,
      // Falls back to English so a partially translated locale never shows a blank label.
      t: (key, vars) => interpolate(dict[key] ?? en[key] ?? key, vars),
      setPreferences,
    };
  }, [preferences, setPreferences]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = React.useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}

export function useT() {
  return useI18n().t;
}
